import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Leaf, Zap, Home, Building, CheckCircle } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import owc from './images1/digester.png';
const comparisonData = [
  {
    parameter: 'Process Type',
    bioguest: 'In-vessel aerobic, natural',
    automatic: 'Thermally accelerated',
    semiAuto: 'Hybrid (machine + yard)',
    home: 'Natural aerobic'
  },
  {
    parameter: 'Heating Element',
    bioguest: 'Not required',
    automatic: 'Required',
    semiAuto: 'Not required',
    home: 'Not required'
  },
  {
    parameter: 'Energy Consumption',
    bioguest: 'Very low',
    automatic: 'High',
    semiAuto: 'Low',
    home: 'Nil'
  },
  {
    parameter: 'Processing Time',
    bioguest: 'Continuous, biological',
    automatic: '~24 hours',
    semiAuto: '3-4 weeks',
    home: 'Gradual'
  },
  {
    parameter: 'Odor Control',
    bioguest: 'Excellent (in-vessel)',
    automatic: 'Moderate',
    semiAuto: 'Depends on management',
    home: 'Excellent'
  },
  {
    parameter: 'Compost Quality',
    bioguest: 'High, stabilized',
    automatic: 'Partially stabilized',
    semiAuto: 'High',
    home: 'High'
  },
  {
    parameter: 'Space Requirement',
    bioguest: 'Low',
    automatic: 'Low',
    semiAuto: 'High (yard needed)',
    home: 'Very low'
  },
  {
    parameter: 'Environmental Impact',
    bioguest: 'Highly sustainable',
    automatic: 'Energy intensive',
    semiAuto: 'Sustainable',
    home: 'Highly sustainable'
  },
  {
    parameter: 'Compliance (SWM 2016)',
    bioguest: 'Yes',
    automatic: 'Conditional',
    semiAuto: 'Yes',
    home: 'Yes'
  }
];

const systems = [
  {
    title: 'BIOGUEST-V',
    subtitle: 'Fully Automatic Natural In-Vessel Composting',
    icon: Building,
    features: [
      'No thermal heaters required',
      'Integrated curing mechanism',
      'Very low power consumption',
      'Odor-free operation',
      'Capacity: 25 kg to 2000 kg/day'
    ],
    description: 'Relies on optimized microbial kinetics, controlled aeration, and internal curing to convert organic waste into compost with minimal energy input.'
  },
  {
    title: '24-Hour Automatic Converter',
    subtitle: 'Thermal Accelerated System',
    icon: Zap,
    features: [
      'Continuous automated operation',
      'High throughput capacity',
      'Rapid processing',
      'Capacity: 100 kg to 5000 kg/day',
      'Accelerated moisture reduction'
    ],
    description: 'Mechanized, thermally accelerated waste processing unit designed to rapidly convert organic waste within a short cycle time.'
  },
  {
    title: 'Semi-Automatic System',
    subtitle: 'Hybrid Composting Solution',
    icon: Leaf,
    features: [
      'Initial mechanical mixing (~2 hours)',
      'Secondary aerobic curing (3-4 weeks)',
      'Lower energy costs',
      'Capacity: 100 kg to 5000 kg/day',
      'Produces stable compost'
    ],
    description: 'Combines short-duration mechanical initiation with yard-based aerobic maturation for optimal composting results.'
  },
  {
    title: 'Home Composting',
    subtitle: 'Non-Electric Solution',
    icon: Home,
    features: [
      'Zero electricity consumption',
      'Compact and aesthetic design',
      'Daily waste loading',
      'Low maintenance',
      'Perfect for households'
    ],
    description: 'Non-electric, compact, in-vessel composting solution intended for households and small establishments.'
  }
];

export const Digestate = () => {
  return (
    <div className="min-h-screen pt-20" data-testid="composting-page">
      {/* Hero Section */}
      <section className="bg-emerald-950 py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-amber-400 uppercase tracking-[0.3em] text-sm font-semibold">
              Organic Waste Solutions
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mt-4 mb-6">
              DIGESTATE MANAGEMENT
            </h1>
            <p className="text-white/70 text-lg max-w-3xl mx-auto tracking-wider leading-relaxed">
              Digestate is the nutrient-rich residual material generated from the anaerobic digestion (AD) process.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Organic Waste Composting Systems (OWC) */}
      <section className="py-12 md:py-10 bg-white dark:bg-emerald-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl font-bold text-emerald-900 dark:text-white mb-6">
                DIGESTATE MANAGEMENT & MANURE UPGRADATION
              </h2>
              <p className="text-slate-600 dark:text-white/70 mb-6 tracking-wider leading-relaxed">
                Digestate is the nutrient-rich residual material generated from the anaerobic digestion (AD) process. While organic matter is converted into biogas, the majority of macro-nutrients (N, P, K) and micro-nutrients remain conserved in the digestate, making it a valuable resource for soil conditioning and sustainable agriculture when properly characterised and managed.
              </p>
              <p className="text-slate-600 dark:text-white/70 mb-6 tracking-wider leading-relaxed">RENVITEK ENGINEERS LLP provides specialised services for digestate analysis, valorisation, and upgradation, enabling Biogas–CBG plant operators to maximise resource recovery and ensure regulatory and agronomic suitability. The fibrous fraction of digestate contributes to soil organic carbon, improving soil structure, aeration, and moisture retention, while the liquid fraction serves as a potential biofertilizer. Effective digestate utilisation reduces dependency on synthetic fertilisers and creates additional revenue opportunities for AD facility owners.</p>
              
              <h3 className="text-xl font-semibold text-emerald-900 dark:text-white mb-4">
                Digestate Valorisation Pathways
              </h3>
              <p className="text-slate-600 dark:text-white/70 mb-6 tracking-wider leading-relaxed">Depending on end-use requirements, digestate can be processed through:</p>
              <ul className="space-y-3">
                {[
                  'Solid–liquid separation for targeted utilisation',
                  'Composting, drying, or pelletisation of the solid fraction',
                  'Direct use or further treatment of the liquid fraction',
                  'Nutrient recovery through ammonia stripping, concentration, or membrane processes',
                  'Fibre recovery for soil conditioners, bio-based products, or fuel applications'
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-emerald-500 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-600 dark:text-white/70">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <img 
                src={owc}
                alt="Composting System"
                className="w-full h-auto rounded-lg shadow-xl"
              />
            </motion.div>
          </div>
        </div>
      </section>

{/* Digestate Analysis & Quality Assessment */}
      <section className="py-12 md:py-10 bg-slate-50 dark:bg-emerald-900/30">
        <div className="w-full px-4 sm:px-6 lg:px-12">
        <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              
            >
              <h2 className="text-3xl font-bold text-emerald-900 dark:text-white mb-6 text-center">Digestate Analysis & Quality Assessment</h2>
          
          <div className="mb-8">
            <p className="space-y-4 text-slate-600 dark:text-white/70 tracking-wider leading-relaxed">Prior to land application or commercialisation, digestate must be scientifically evaluated. RENVITEK offers comprehensive chemical and biological analysis packages, including:</p>
            <p className="space-y-4 text-slate-600 dark:text-white/70 tracking-wider leading-relaxed"><b>Benefits:</b>
            <ul className="list-disc pl-6 space-y-4 text-slate-600 dark:text-white/70 leading-relaxed">
              <li>Physicochemical parameters: Total solids, volatile solids, pH, COD, BOD</li>
              <li>Nutrient profile: Total nitrogen, ammoniacal nitrogen, phosphorus, potassium, and micronutrients</li>
              <li>Trace elements and heavy metals for regulatory compliance</li>
              <li>Volatile Fatty Acids (VFAs): Indicators of digestion stability</li>
              <li>Residual Biogas Potential (RBP): Assessment of unconverted organic matter</li>
              <li>Microbial and phytotoxicity tests: Suitability for agricultural application</li>
            </ul></p>
            </div>
        </motion.div>
        </div>
      </section>
      {/* Solid & Liquid Fermented Organic Manure (SOFM & LOFM) */}
      <section className="py-12 md:py-10 bg-white-50 dark:bg-emerald-900/30">
        <div className="w-full px-4 sm:px-6 lg:px-12">
        <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              
            >
              <h2 className="text-3xl font-bold text-emerald-900 dark:text-white mb-6 text-center">Solid & Liquid Fermented Organic Manure (SOFM & LOFM)</h2>
          
          <div className="mb-8">
            <p className="space-y-4 text-slate-600 dark:text-white/70 tracking-wider leading-relaxed">Biogas–CBG plants also generate solid residues, scum, and liquid digestate, which can be converted into value-added manure products as per SATAT guidelines.
            <ul className="list-disc pl-6 space-y-4 text-slate-600 dark:text-white/70 leading-relaxed">
              <li>The solid fraction, including digested biomass and scum, can be processed through controlled composting or vermicomposting to produce Solid Fermented Organic Manure (SOFM).</li>
              <li>The liquid fraction (digestate/effluent) can be stabilised and enriched to produce Liquid Fermented Organic Manure (LOFM) for agricultural and horticultural use.</li>
            </ul></p>
            </div>
        </motion.div>
        </div>
      </section>
      {/* RENVITEK’s SOFM & LOFM Services */}
      <section className="py-12 md:py-10 bg-slate-50 dark:bg-emerald-900/30">
        <div className="w-full px-4 sm:px-6 lg:px-12">
        <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              
            >
              <h2 className="text-3xl font-bold text-emerald-900 dark:text-white mb-6 text-center">RENVITEK’s SOFM & LOFM Services</h2>
          
          <div className="mb-8">
            <p className="space-y-4 text-slate-600 dark:text-white/70 tracking-wider leading-relaxed">RENVITEK provides end-to-end technical support for SOFM and LOFM development, including:
            <ul className="list-disc pl-6 space-y-4 text-slate-600 dark:text-white/70 leading-relaxed">
              <li>Scientific characterisation and nutrient profiling</li>
              <li>Composting, vermicomposting, and digestate stabilisation strategies</li>
              <li>Controlled enrichment using microbial cultures</li>
              <li>Macro- and micro-nutrient fortification to achieve specified limits</li>
              <li>Process optimisation for uniform quality and market readiness</li>
            </ul></p>
            <p className="space-y-4 text-slate-600 dark:text-white/70 tracking-wider leading-relaxed"><b>Benefits:</b>
            <ul className="list-disc pl-6 space-y-4 text-slate-600 dark:text-white/70 leading-relaxed">
              <li>Conversion of biogas by-products into certified organic manures</li>
              <li>Compliance with SATAT and applicable fertiliser guidelines</li>
              <li>Improved soil fertility and crop productivity</li>
              <li>Reduced chemical fertiliser usage</li>
              <li>Additional revenue streams for Biogas–CBG plants</li>
              <li>Complete alignment with circular economy principles</li>
            </ul></p>
            </div>
        </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 md:py-10 bg-amber-500">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-emerald-950 mb-4">
            Ready to Implement Composting Solutions?
          </h2>
          <p className="text-emerald-900 text-lg mb-8 tracking-wider">
            Contact us to discuss the right composting system for your requirements.
          </p>
          <Link to="/contact">
            <motion.button
              className="bg-emerald-900 hover:bg-emerald-800 text-white px-10 py-4 uppercase tracking-wider font-semibold inline-flex items-center gap-2"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              data-testid="composting-cta-btn"
            >
              Get in Touch
              <ArrowRight size={20} />
            </motion.button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Digestate;
